<script>
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import UnavailabilityCard from '../staffs/UnavailabilityCard.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CreateUnavailability from '../staffs/CreateUnavailability.vue';
import { format } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import LinkBranchServiceModal from './LinkBranchServiceModal.vue';

export default {
  name: "BranchStaffDetails",
  components: {
    BackButton,
    LoadingState,
    ErrorState,
    EmptyState,
    UnavailabilityCard,
    PaginationBar,
    DeleteModal,
    CreateUnavailability,
    LinkBranchServiceModal,
  },
  data() {
    return {
      showCreateModal: false,
      showDeleteModal: false,
      deleteName: null,
      deleteId: null,
      isDeleting: false,
      showEditModal: false,
      selectedUnavailablity: null,
      staffDetails: null,
      showLinkModal: false,
    };
  },
  computed: {
    staffId() {
      return this.$route.params.staffId;
    },
    branchId() {
      return this.$route.params.branchId;
    },
    unavailabilityList() {
      return this.$store.state.staffV2.unavailabilityList || [];
    },
    isLoading() {
      return this.$store.state.staffV2.isLoadingUnavailability || false;
    },
    isError() {
      return this.$store.state.staffV2.isErrorUnavailability || false;
    },
    pagination() {
      return this.$store.getters['staffV2/formattedUnavailabilityPagination'];
    },
    branchName() {
      const branch = this.staffDetails?.branches?.find(
        b => b.id === this.$route.params.branchId
      );

      return branch ? branch.name : 'Branch Management';
    },
    staffBranchServiceList() {
      const list = this.staffDetails?.services?.filter(
        s => s.branches?.some(b => b.id === this.branchId)
      ) || [];

      return list;
    },
    staffName() {
      return this.staffDetails?.staff?.name || 'Staff';
    },
    staffGender() {
      return this.staffDetails?.staff?.staff_metadata?.gender || null;
    },
    staffSeniority() {
      return this.staffDetails?.staff?.staff_metadata?.seniority || null;
    }
  },
  methods: {
    formatDate(dateString) {
      try {
        return format(new Date(dateString), 'yyyy-MM-dd'); 
      } catch (e) {
        return '-';
      }
    },
    async fetchStaff() {
      try {
        this.staffDetails = await this.$store.dispatch('staffV2/getStaff', this.staffId);
      } catch (err) {
        useAlert(`Failed to fetch staff: ${err}`);
      }
    },
    async fetchUnavailability() {
      try {
        await this.$store.dispatch('staffV2/fetchStaffUnavailability', { staffId: this.staffId });
      } catch (err) {
        useAlert(`Failed to fetch unavailability: ${err}`);
      }
    },
    handleOpenDelete(id) {
      this.deleteName = 'this unavailability';
      this.deleteId = id;
      this.showDeleteModal = true;
    },
    handleCloseDelete() {
      this.deleteName = null;
      this.deleteId = null;
      this.showDeleteModal = false;
    },
    async handleConfirmDelete() {
      try {
        this.isDeleting = true;
        await this.$store.dispatch('staffV2/deleteStaffUnavailability', { staffId: this.staffId, unavailabilityId: this.deleteId });
        this.isDeleting = false;
        await this.$store.dispatch('staffV2/fetchStaffUnavailability', { staffId: this.staffId });
        useAlert('Unavailability deleted');
        this.handleCloseDelete();
      } catch (err) {
        useAlert(`Failed to delete unavailability: ${err}`);
      } finally {
        this.isDeleting = false;
      }
    },
    handleOpenEdit(item) {
      this.selectedUnavailablity = item;
      this.showEditModal = true;
    },
    handleCloseEdit() {
      this.selectedUnavailablity = null;
      this.showEditModal = false;
    },
    async refreshPage() {
      await this.fetchStaff();
      await this.fetchUnavailability();
    }
  },
  async mounted() {
    await this.refreshPage();
  }
};
</script>

<template>
<div class="flex-1 overflow-auto p-8">
  <!-- Modals -->
  <LinkBranchServiceModal
    v-if="showLinkModal"
    :services="staffBranchServiceList"
    @close="showLinkModal = false"
    @refresh="refreshPage"
  />

  <!-- Page Header -->
  <div class="flex justify-between items-center h-14 min-h-[3.5rem] px-4 py-2 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800/50">
    <h1 class="flex items-center mb-0 text-2xl text-slate-900 dark:text-slate-100">
      <BackButton class="ml-2 mr-4" />
      <fluent-icon icon="building-bank" class="hidden ml-1 mr-2 rtl:ml-2 rtl:mr-1 md:block" />
      <span class="text-2xl font-medium text-slate-900 dark:text-slate-100">
        {{ branchName }} / Staff / {{ staffName }}
      </span>
    </h1>
  </div>

  <!-- Staff Details Grid -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">

    <!-- LEFT SIDE -->
    <div class="lg:col-span-2 space-y-6">

      <!-- Staff Info -->
      <div v-if="staffDetails" class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow">
        <h2 class="text-lg md:text-xl font-semibold mb-4">Staff Details</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="space-y-4">
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">Name</p>
              <p class="font-medium">{{ staffDetails.staff.name }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">Status</p>
              <span
                class="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="staffDetails.staff.is_active
                  ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                  : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'"
              >
                {{ staffDetails.staff.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div v-if="staffGender">
              <p class="text-sm text-gray-500 dark:text-gray-400">Gender</p>
              <p>{{ staffGender }}</p>
            </div>
          </div>
          <div class="space-y-4">
            <div v-if="staffSeniority">
              <p class="text-sm text-gray-500 dark:text-gray-400">Seniority</p>
              <p>{{ staffSeniority }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">Created</p>
              <p>{{ formatDate(staffDetails.staff.created_at) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">Updated</p>
              <p>{{ formatDate(staffDetails.staff.updated_at) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Unavailability -->
      <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
        <h2 class="text-lg md:text-xl font-semibold">Unavailability</h2>

        <PaginationBar
          v-if="!isLoading && !isError && unavailabilityList.length"
          :pagination="pagination"
          :loading="isLoading"
          @previous="fetchPreviousPage"
          @next="fetchNextPage"
          @page-size-change="updatePageSize"
        />

        <LoadingState v-if="isLoading" message="Loading unavailability..." />
        <ErrorState v-else-if="isError" />
        <EmptyState
          v-else-if="!isLoading && unavailabilityList.length === 0"
          title="No unavailability found"
          message="Manage unavailability at Staff Management page"
        />

        <div v-else class="grid gap-4">
          <UnavailabilityCard
            v-for="item in unavailabilityList"
            :key="item.id"
            :unavailability="item"
            @edit="handleOpenEdit"
            @delete="handleOpenDelete"
            :showActions="false"
          />
        </div>
      </div>

    </div>

    <!-- RIGHT SIDE -->
    <div class="space-y-6 h-full">

      <!-- Branch Info -->
      <!-- <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow">
        <h2 class="text-lg md:text-xl font-semibold mb-4">Branch</h2>
        <p class="text-gray-500 dark:text-gray-400">
          <p class="text-gray-500 dark:text-gray-400">
            {{ staffDetails.branches && staffDetails.branches[0] ? staffDetails.branches[0].name : 'No branch assigned' }}
          </p>
        </p>
      </div> -->

      <!-- Services -->
      <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow h-full space-y-4">
        <div class="flex gap-4 justify-between items-center">
          <h2 class="text-lg md:text-xl font-semibold">Services</h2>
          <woot-button icon="add" @click="showLinkModal = true">Assign Services</woot-button>
        </div>
        <ul v-if="staffBranchServiceList.length" class="space-y-2 max-h-60 overflow-y-auto">
          <li
            v-for="s in staffBranchServiceList"
            :key="s.id"
            class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700"
          >
            {{ s.name }}
          </li>
        </ul>
        <p v-else class="text-gray-500 text-center py-4">No services assigned</p>
      </div>

    </div>

  </div>
</div>
</template>